import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { isIos, isStandalone } from '../../lib/device';
import { Button, Card } from '../../components/ui';
import { useT } from '../../i18n';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
  });
}

export function InstallCard() {
  const [canPrompt, setCanPrompt] = useState(!!deferred);
  const t = useT();
  const [hidden, setHidden] = useState(() => isStandalone() || localStorage.getItem('rw:install-dismissed') === '1');

  useEffect(() => {
    const on = (e: Event) => {
      e.preventDefault();
      deferred = e as BeforeInstallPromptEvent;
      setCanPrompt(true);
    };
    window.addEventListener('beforeinstallprompt', on);
    window.addEventListener('appinstalled', () => setHidden(true));
    return () => window.removeEventListener('beforeinstallprompt', on);
  }, []);

  if (hidden || (!canPrompt && !isIos())) return null;
  const dismiss = () => {
    localStorage.setItem('rw:install-dismissed', '1');
    setHidden(true);
  };
  return (
    <Card className="mt-4 flex items-center gap-3">
      <img src="/icons/icon-192.png" alt="" className="size-12 rounded-xl" />
      <div className="flex-1 text-sm">
        <div className="font-semibold">{t('Install Runner Wars')}</div>
        <div className="text-ink-400">
          {t(canPrompt ? 'Full screen, faster start, works offline.' : 'Tap Share → “Add to Home Screen”.')}{' '}
          <Link to="/guide#cai-dat" className="font-semibold text-volt" data-testid="install-guide-link">
            {t('See how to install')}
          </Link>
        </div>
      </div>
      {canPrompt ? (
        <Button
          size="sm"
          onClick={async () => {
            await deferred?.prompt();
            const choice = await deferred?.userChoice;
            if (choice?.outcome === 'accepted') setHidden(true);
            deferred = null;
            setCanPrompt(false);
          }}
        >
          {t('Install')}
        </Button>
      ) : null}
      <button onClick={dismiss} className="px-1 text-ink-500" aria-label={t('Dismiss')}>
        ✕
      </button>
    </Card>
  );
}
